// Pendientes — the general "esto falta" entity.
//
// A pendiente is any deliverable or commitment the despacho is chasing that is
// NOT already modelled by a dedicated surface: un acta de entrega, un convenio
// modificatorio, un endoso que pidieron, o un compromiso que el agente firmó
// con la afianzadora para poder emitir (entregar el comprobante de domicilio
// actualizado y cosas del estilo).
//
// Why its own top-level collection instead of an array on the parent:
//   - The section is a cross-cartera chase list. One query ordered by
//     fechaLimite beats fanning out over every contract's arrays.
//   - A pendiente attaches to four different parents (cliente, contrato,
//     fianza, convenio), so there is no single array that could hold them all.
//
// Relationship to fianza.endososPendientes[] (utils/fianzaEventos.js): that one
// STAYS where it is. Resolving an endoso request and appending the evento must
// be one atomic write and they only share an object at the fianza level. The
// Pulso tab and this page read both and normalize the endoso entries through
// `pendienteFromEndoso` so the user sees one list.
//
// Pure functions only — no React, no Firestore imports. Callers persist the
// returned objects.

export const PENDIENTE_TIPOS = {
  ACTA_ENTREGA: 'acta_entrega',
  CONVENIO_MODIFICATORIO: 'convenio_modificatorio',
  ENDOSO: 'endoso',
  COMPROMISO_AFIANZADORA: 'compromiso_afianzadora',
  DOCUMENTO: 'documento',
  OTRO: 'otro',
};

export const PENDIENTE_TIPO_LABELS = {
  acta_entrega: 'Acta de entrega',
  convenio_modificatorio: 'Convenio modificatorio',
  endoso: 'Endoso',
  compromiso_afianzadora: 'Compromiso con la afianzadora',
  documento: 'Documento',
  otro: 'Otro',
};

// The tipos an agente can CREATE. `endoso` is deliberately not one of them: an
// endoso only ever lives on a fianza and it already has its own flow there
// (Solicitar endoso → fianza.endososPendientes[]); the list merely shows those
// rows normalized through pendienteFromEndoso. Offering it here produced
// endosos hanging off a cliente, which is meaningless.
export const TIPOS_CREABLES = [
  PENDIENTE_TIPOS.COMPROMISO_AFIANZADORA,
  PENDIENTE_TIPOS.DOCUMENTO,
  PENDIENTE_TIPOS.ACTA_ENTREGA,
  PENDIENTE_TIPOS.CONVENIO_MODIFICATORIO,
  PENDIENTE_TIPOS.OTRO,
];

/** @param {string} tipo @return {boolean} */
export function esTipoCreable(tipo) {
  return TIPOS_CREABLES.includes(tipo);
}

// There is no "quién lo debe" field. An earlier version had one (fiado /
// agente / afianzadora / beneficiario) with a dropdown, a column and a filter,
// and it earned none of that: the only question it ever answered was whether
// the thing can be asked of the fiado, and that follows from the tipo. It also
// got the endoso wrong — it attributed one to the afianzadora, when an endoso
// is something the AGENTE processes, and that miscall was what hid the email
// button on endoso rows.
//
// What can be asked of the fiado by email: an acta, a convenio modificatorio,
// a documento, or a free-form otro. NOT an endoso (handled with the
// afianzadora) and NOT a compromiso con la afianzadora (Fran, 2026-09-28:
// mailing "falta un documento" with no link to upload it is pointless; the
// compromiso is our promise to the carrier and the paper is chased through
// the expediente flow, which does carry an upload link).
const NO_SE_PIDE_AL_FIADO = [
  PENDIENTE_TIPOS.ENDOSO,
  PENDIENTE_TIPOS.COMPROMISO_AFIANZADORA,
];

/**
 * Whether this pendiente is something the fiado can be asked for — the gate on
 * the email action, and on the portal's "te toca a ti" grouping.
 *
 * @param {string} tipo
 * @return {boolean}
 */
export function sePuedePedirAlFiado(tipo) {
  return !NO_SE_PIDE_AL_FIADO.includes(tipo);
}

export const PENDIENTE_ESTATUS = {
  ABIERTO: 'abierto',
  EN_PROCESO: 'en_proceso',
  RESUELTO: 'resuelto',
  CANCELADO: 'cancelado',
};

export const PENDIENTE_ESTATUS_LABELS = {
  abierto: 'Abierto',
  en_proceso: 'En proceso',
  resuelto: 'Resuelto',
  cancelado: 'Cancelado',
};

// Still awaiting action. Mirrors ABIERTOS in fianzaEventos.js.
const ABIERTOS = [PENDIENTE_ESTATUS.ABIERTO, PENDIENTE_ESTATUS.EN_PROCESO];

export const TARGET_TIPOS = {
  ORG: 'org',
  CLIENTE: 'cliente',
  CONTRATO: 'contrato',
  FIANZA: 'fianza',
  CONVENIO: 'convenio',
};

export const TARGET_TIPO_LABELS = {
  org: 'Organización',
  cliente: 'Fiado',
  contrato: 'Contrato',
  fianza: 'Fianza',
  convenio: 'Convenio',
};

// THE TIPO DICTATES THE LEVEL (Fran, 2026-09-28: "un endoso a nivel cliente es
// ridículo"). Which target a tipo may hang from, narrowest first:
//   - compromiso: the afianzadora asks the FIADO for something, so it belongs
//     to the cliente, named with the afianzadora. It can be born from a fianza
//     (that is where they ask), and then contratoId/fianzaId are kept as the
//     ORIGIN reference, but the target is the cliente.
//   - documento: the fiado's expediente → cliente.
//   - acta de entrega: closes an obra → contrato, or the cliente when the obra
//     is not modelled as a contrato.
//   - convenio modificatorio: amends a contrato → contrato only.
//   - otro: anything, anywhere, including the despacho's own (org).
//   - endoso: fianza only, and not creatable here (see TIPOS_CREABLES).
export const NIVELES_POR_TIPO = {
  compromiso_afianzadora: ['cliente'],
  documento: ['cliente'],
  acta_entrega: ['contrato', 'cliente'],
  convenio_modificatorio: ['contrato'],
  otro: ['fianza', 'contrato', 'convenio', 'cliente', 'org'],
  endoso: ['fianza'],
};

/** @param {string} tipo @return {Array<string>} */
export function nivelesParaTipo(tipo) {
  return NIVELES_POR_TIPO[tipo] || NIVELES_POR_TIPO.otro;
}

/** @param {string} tipo @param {string} nivel @return {boolean} */
export function tipoPermiteNivel(tipo, nivel) {
  return nivelesParaTipo(tipo).includes(nivel);
}

// Narrowest level first when picking a target from a context.
const ORDEN_NIVELES = ['fianza', 'contrato', 'convenio', 'cliente', 'org'];

/**
 * Pick the target for a tipo out of what the calling surface knows. Walks the
 * levels narrowest-first and returns the first one the tipo allows AND the
 * context can name. `null` when the tipo needs a relation the context lacks
 * (e.g. a convenio modificatorio with no contrato).
 *
 * @param {string} tipo
 * @param {{clienteId?:string, contratoId?:string, fianzaId?:string,
 *   convenioId?:string}} ctx
 * @return {{targetTipo:string, targetId:string|null}|null}
 */
export function resolverTarget(tipo, ctx = {}) {
  const ids = {
    fianza: ctx.fianzaId || null,
    contrato: ctx.contratoId || null,
    convenio: ctx.convenioId || null,
    cliente: ctx.clienteId || null,
  };
  const permitidos = nivelesParaTipo(tipo);
  for (const nivel of ORDEN_NIVELES) {
    if (!permitidos.includes(nivel)) continue;
    if (nivel === TARGET_TIPOS.ORG) return { targetTipo: TARGET_TIPOS.ORG, targetId: null };
    if (ids[nivel]) return { targetTipo: nivel, targetId: ids[nivel] };
  }
  return null;
}

// Label for a pendiente with no relation. It stands where the fiado name goes
// in every list, so it reads as "whose" rather than "what".
export const ORG_LABEL = 'Organización';

/**
 * A pendiente that belongs to the despacho itself — "llamar a Chubb el jueves",
 * "renovar la cédula" — with no fiado, contrato, fianza or convenio behind it.
 * `targetTipo === 'org'` is the stored marker; the field fallback covers rows
 * assembled before the marker existed.
 *
 * @param {Object} p
 * @return {boolean}
 */
export function esDeLaOrg(p) {
  if (!p) return false;
  if (p.targetTipo === TARGET_TIPOS.ORG) return true;
  return !p.targetTipo && !p.clienteId && !p.convenioId;
}

/**
 * Whether the tipo needs an afianzadora named. A compromiso is a promise made
 * TO a carrier, so without one there is nothing to roll up and no one to owe.
 *
 * @param {string} tipo
 * @return {boolean}
 */
export function requiereAfianzadora(tipo) {
  return tipo === PENDIENTE_TIPOS.COMPROMISO_AFIANZADORA;
}

/**
 * Form-level validation shared by the dialog. Returns the message to show, or
 * null when the pendiente can be written.
 *
 * @param {{tipo:string, titulo:string, afianzadora?:string|null}} args
 * @return {string|null}
 */
export function validarPendiente({ tipo, titulo, afianzadora = null, targetTipo = undefined }) {
  if (!esTipoCreable(tipo)) return 'Ese tipo no se registra aquí.';
  if (!String(titulo || '').trim()) return 'Dinos qué falta.';
  if (requiereAfianzadora(tipo) && !afianzadora) {
    return 'Dinos con qué afianzadora es el compromiso.';
  }
  if (targetTipo !== undefined) {
    if (!targetTipo) {
      return nivelesParaTipo(tipo).includes('contrato') && !nivelesParaTipo(tipo).includes('cliente')
        ? 'Elige el contrato.'
        : 'Elige el fiado.';
    }
    if (!tipoPermiteNivel(tipo, targetTipo)) {
      return `${PENDIENTE_TIPO_LABELS[tipo] || tipo} no va a nivel ${TARGET_TIPO_LABELS[targetTipo] || targetTipo}.`;
    }
  }
  return null;
}

// Per-tipo defaults: whether an afianzadora is asked for. There is NO
// portal visibility any more — pendientes are internal to the team (Fran,
// 2026-09-28: "los fiados no deben poder ver los pendientes en ninguna
// circunstancia"). The fiado is reached by the agente, by email, on purpose.
export const DEFAULTS_POR_TIPO = {
  compromiso_afianzadora: { pideAfianzadora: true },
  acta_entrega: { pideAfianzadora: false },
  convenio_modificatorio: { pideAfianzadora: false },
  documento: { pideAfianzadora: false },
  endoso: { pideAfianzadora: true },
  otro: { pideAfianzadora: false },
};

/**
 * The defaults for a tipo, falling back to `otro` for an unknown one.
 * @param {string} tipo
 * @return {Object}
 */
export function defaultsParaTipo(tipo) {
  return DEFAULTS_POR_TIPO[tipo] || DEFAULTS_POR_TIPO.otro;
}

// The 14 expediente document types, as titulo suggestions. A compromiso con la
// afianzadora is almost always one of these ("entrégame el comprobante de
// domicilio actualizado"), so offering them as chips turns the common case into
// two clicks instead of typing. Keep in lockstep with REQUIRED_FILES in
// fianzas/src/pages/ClienteDetails.js and SOLICITUD_DOC_LABELS in
// functions/index.js.
export const DOCS_SUGERIDOS = [
  'Comprobante de Domicilio',
  'Constancia de Situación Fiscal',
  'Acta Constitutiva',
  'Actas de Asambleas',
  'Poderes',
  'Identificación oficial del apoderado',
  'Acuse de Recibo de Declaración Anual',
  'Declaración del Ejercicio',
  'Estados Financieros (Contador)',
  'Estados Financieros Parciales (Contador)',
  'Cédula Profesional del Contador',
  'Currículum Empresarial',
];

// Suggestions per tipo. Only the document-shaped tipos get the expediente list;
// an acta de entrega or a convenio modificatorio IS the document, so its title
// is about which obra or which contrato, not which doc type.
export const SUGERENCIAS_POR_TIPO = {
  compromiso_afianzadora: DOCS_SUGERIDOS,
  documento: DOCS_SUGERIDOS,
  acta_entrega: ['Acta de entrega general', 'Acta de entrega parcial', 'Acta de recepción'],
  convenio_modificatorio: [
    'Convenio modificatorio de monto',
    'Convenio modificatorio de plazo',
    'Convenio modificatorio firmado',
  ],
  endoso: [],
  otro: [],
};

export const TITULO_MAX_LEN = 140;
export const DETALLE_MAX_LEN = 1000;
export const NOTA_MAX_LEN = 500;

/**
 * Build a new pendiente ready to write. `org` is required by the rules and is
 * NOT defaulted here — a pendiente without an org is invisible to everyone.
 *
 * @param {Object} args
 * @return {Object}
 */
export function buildPendiente({
  org,
  targetTipo = TARGET_TIPOS.ORG,
  targetId = null,
  clienteId = null,
  convenioId = null,
  contratoId = null,
  fianzaId = null,
  tipo = PENDIENTE_TIPOS.OTRO,
  titulo = '',
  detalle = '',
  afianzadora = null,
  afianzadoraRaw = null,
  fechaLimite = null,
  asignadoA = null,
  asignados = null,
  createdBy = null,
  // Órdenes de Trabajo: a pendiente filed from an OT keeps the OT id and,
  // when it is a request to another equipo, that equipo's id. Both are
  // context only — a pendiente never moves the OT.
  otId = null,
  equipoId = null,
  ts = null,
}) {
  const now = ts || Date.now();
  // Several people can carry one pendiente, like assignedUsers on a cliente.
  // `asignadoA` stays as the first of them so every consumer that still reads
  // the single field (scheduler 'mios', older rows) keeps working.
  const lista = normalizarAsignados({ asignados, asignadoA });
  // No relation means the despacho's own pendiente: every relation id is
  // dropped so nothing downstream mistakes it for a fiado's.
  const deLaOrg = targetTipo === TARGET_TIPOS.ORG || !targetId;
  return {
    org,
    targetTipo: deLaOrg ? TARGET_TIPOS.ORG : targetTipo,
    targetId: deLaOrg ? null : targetId,
    clienteId: deLaOrg ? null : clienteId,
    convenioId: deLaOrg ? null : convenioId,
    contratoId: deLaOrg ? null : contratoId,
    fianzaId: deLaOrg ? null : fianzaId,
    tipo,
    titulo: titulo.trim().slice(0, TITULO_MAX_LEN),
    // What goes in the email to the fiado when the agente asks for it;
    // notasInternas is the team's thread and never leaves the CRM.
    detalle: detalle.trim().slice(0, DETALLE_MAX_LEN) || null,
    notasInternas: [],
    // Canonical trade name (mapAfianzadoraName) so the por-afianzadora rollup
    // cannot fragment; the raw XML emisor is kept beside it so nothing the
    // document actually said is lost.
    afianzadora: afianzadora || null,
    afianzadoraRaw: afianzadoraRaw || null,
    estatus: PENDIENTE_ESTATUS.ABIERTO,
    fechaLimite: fechaLimite || null,
    asignados: lista,
    asignadoA: lista[0] || null,
    // Manual sends only. `ultimoEnvioAt` doubles as the cooldown clock.
    correos: { enviados: 0, ultimoEnvioAt: null, ultimoTo: null },
    resueltoCon: null,
    resueltoAt: null,
    resueltoPor: null,
    otId: otId || null,
    equipoId: otId ? (equipoId || null) : null,
    createdBy,
    createdAt: now,
    updatedAt: now,
    ...buildActividad('creado', createdBy, null, now),
  };
}

/**
 * Who carries this pendiente, as an array, whatever shape the doc has:
 * `asignados[]` (current) or the older single `asignadoA`.
 *
 * @param {Object} p
 * @return {Array<string>}
 */
export function normalizarAsignados(p) {
  if (!p) return [];
  if (Array.isArray(p.asignados)) {
    return [...new Set(p.asignados.filter((u) => typeof u === 'string' && u))];
  }
  return p.asignadoA ? [p.asignadoA] : [];
}

/** @param {Object} p @param {string} uid @return {boolean} */
export function estaAsignado(p, uid) {
  return Boolean(uid) && normalizarAsignados(p).includes(uid);
}

/**
 * The update payload for a new set of assignees. Keeps `asignadoA` in
 * lockstep as the first one.
 *
 * @param {Array<string>} uids
 * @param {number} [ts]
 * @param {string|null} [por]  - uid making the assignment
 * @param {{uid?:string, nombre?:string}|null} [quien]  - for ultimaActividad
 * @param {string|null} [texto]  - assignee names, for the feed
 * @return {Object}
 */
export function buildAsignacion(uids, ts = null, por = null, quien = null, texto = null) {
  const lista = [...new Set((uids || []).filter((u) => typeof u === 'string' && u))];
  const now = ts || Date.now();
  return {
    asignados: lista,
    asignadoA: lista[0] || null,
    // Who made the assignment. The onPendienteAsignado trigger mails the
    // newly added people and skips this uid, so assigning yourself is silent.
    asignacionPor: por || null,
    updatedAt: now,
    ...buildActividad('asignado', quien || { uid: por }, texto, now),
  };
}

/** @param {Object} p @return {boolean} */
export function isAbierto(p) {
  return ABIERTOS.includes(p?.estatus);
}

/**
 * Urgency from the due date. Same buckets and thresholds as
 * clasificarEndosoPendiente so a merged list sorts consistently.
 *
 * @param {Object} pendiente
 * @param {number} [nowMs]
 * @return {{code:'vencido'|'urgente'|'proximo'|'sinFecha'|'normal', dias:number|null}}
 */
export function clasificarPendiente(pendiente, nowMs = Date.now()) {
  const limite = pendiente?.fechaLimite ? Number(pendiente.fechaLimite) : null;
  if (!limite || Number.isNaN(limite)) return { code: 'sinFecha', dias: null };
  const dias = Math.ceil((limite - nowMs) / 86400000);
  if (dias < 0) return { code: 'vencido', dias };
  if (dias <= 7) return { code: 'urgente', dias };
  if (dias <= 30) return { code: 'proximo', dias };
  return { code: 'normal', dias };
}

// Overdue first, then soonest; undated last — they have no clock running.
const ORDEN_URGENCIA = { vencido: 0, urgente: 1, proximo: 2, normal: 3, sinFecha: 4 };

/**
 * Sort a list of rows already carrying `urgencia`.
 *
 * @param {Array<Object>} rows
 * @return {Array<Object>} the same array, sorted in place
 */
export function ordenarPorUrgencia(rows) {
  return rows.sort((a, b) => {
    const ua = ORDEN_URGENCIA[a.urgencia] ?? 9;
    const ub = ORDEN_URGENCIA[b.urgencia] ?? 9;
    if (ua !== ub) return ua - ub;
    return (a.fechaLimite || Infinity) - (b.fechaLimite || Infinity);
  });
}

/**
 * Decorate a stored pendiente with the fields the table and the cards read.
 * Naming matches the endosos rows in usePulsoData so both can share a renderer.
 *
 * @param {Object} pendiente  - the doc, with `id`
 * @param {Object} ctx  - {clientName, contractTitle, convenioNombre, assignedUsers}
 * @param {number} [nowMs]
 * @return {Object}
 */
export function decorarPendiente(pendiente, ctx = {}, nowMs = Date.now()) {
  const urgencia = clasificarPendiente(pendiente, nowMs);
  return {
    ...pendiente,
    origen: 'pendiente',
    urgencia: urgencia.code,
    diasRestantes: urgencia.dias,
    clientName: ctx.clientName || (esDeLaOrg(pendiente) ? ORG_LABEL : ''),
    contractTitle: ctx.contractTitle || '',
    convenioNombre: ctx.convenioNombre || '',
    isConvenio: Boolean(pendiente.convenioId),
    assignedUsers: ctx.assignedUsers || [],
  };
}

/**
 * Normalize a legacy fianza.endososPendientes[] entry into a pendiente-shaped
 * row so one list can show both. Read-only: these rows are edited through
 * SolicitarEndosoDialog / EndosoDialog, never written back through this module.
 *
 * `id` is namespaced so it can never collide with a real pendiente doc id, and
 * `origen` is what the UI keys off to route the row's actions.
 *
 * @param {Object} row  - a decorated endoso row from usePulsoData
 * @return {Object}
 */
export function pendienteFromEndoso(row) {
  return {
    ...row,
    id: `endoso:${row.contractId}:${row.fianzaId}:${row.id}`,
    // The request's own id inside fianza.endososPendientes[], so a merged
    // list can still act on it (cancel) through mutateFianza.
    endosoReqId: row.id,
    origen: 'endoso',
    tipo: PENDIENTE_TIPOS.ENDOSO,
    titulo: row.descripcion ||
      (Array.isArray(row.tipos) && row.tipos.length
        ? 'Endoso solicitado'
        : 'Endoso pendiente'),
    detalle: row.descripcion || null,
    estatus: row.estatus === 'en_proceso'
      ? PENDIENTE_ESTATUS.EN_PROCESO
      : PENDIENTE_ESTATUS.ABIERTO,
    // usePulsoData names these contractId/clientId; the pendientes model uses
    // contratoId/clienteId. Normalize BOTH here or every consumer that reads a
    // merged list silently gets undefined on endoso rows.
    contratoId: row.contractId,
    clienteId: row.clientId || null,
    targetTipo: TARGET_TIPOS.FIANZA,
    targetId: row.fianzaId,
  };
}

/**
 * Tile counts for the page header and the Pulso tab badge.
 *
 * @param {Array<Object>} rows  - decorated, open rows only
 * @return {Object}
 */
export function contarPendientes(rows) {
  return {
    total: rows.length,
    vencidos: rows.filter((r) => r.urgencia === 'vencido').length,
    urgentes: rows.filter((r) => r.urgencia === 'urgente').length,
    proximos: rows.filter((r) => r.urgencia === 'proximo').length,
    sinFecha: rows.filter((r) => r.urgencia === 'sinFecha').length,
    // What the tab badge turns red for.
    needAttention: rows.filter(
      (r) => r.urgencia === 'vencido' || r.urgencia === 'urgente'
    ).length,
  };
}

/**
 * Compromisos rolled up per afianzadora — the view an owner actually asks for.
 *
 * A compromiso is not a chase item like the others: it is an obligation the
 * despacho carries WITH A CARRIER, and the consequence of letting them pile up
 * is that the afianzadora stops letting you emit against a promise. So the
 * question is never "which fiado owes me", it is "how much do I owe Chubb".
 *
 * `montoExpuesto` sums the monto afianzado of the fianzas standing on these
 * compromisos — the number that makes an owner act. It only counts rows that
 * carry a fianzaId AND whose monto could be resolved, so `montoParcial` flags
 * when the figure understates reality rather than presenting it as complete.
 *
 * @param {Array<Object>} rows  - decorated pendientes (any tipo; filtered here)
 * @param {Object} [opts]
 * @param {function(Object): number|null} [opts.montoDeFianza]
 *   Resolves a row to its fianza's monto afianzado. Injected because this
 *   module stays free of Firestore and of the contracts cache.
 * @return {Array<Object>} one entry per afianzadora, worst first
 */
export function agruparCompromisosPorAfianzadora(rows, opts = {}) {
  const montoDeFianza = opts.montoDeFianza || (() => null);
  const porAfianzadora = new Map();

  rows
    .filter((r) => r.tipo === PENDIENTE_TIPOS.COMPROMISO_AFIANZADORA)
    .filter(isAbierto)
    .forEach((r) => {
      const key = r.afianzadora || 'Sin afianzadora';
      if (!porAfianzadora.has(key)) {
        porAfianzadora.set(key, {
          afianzadora: key,
          total: 0,
          vencidos: 0,
          urgentes: 0,
          sinFecha: 0,
          montoExpuesto: 0,
          montoParcial: false,
          masViejoDias: 0,
          fiados: new Set(),
          rows: [],
        });
      }
      const g = porAfianzadora.get(key);
      g.total += 1;
      if (r.urgencia === 'vencido') g.vencidos += 1;
      if (r.urgencia === 'urgente') g.urgentes += 1;
      if (r.urgencia === 'sinFecha') g.sinFecha += 1;
      if (r.clientName) g.fiados.add(r.clientName);
      g.rows.push(r);

      const monto = montoDeFianza(r);
      if (monto === null || monto === undefined || Number.isNaN(Number(monto))) {
        g.montoParcial = true;
      } else {
        g.montoExpuesto += Number(monto);
      }

      // Age, not time-to-due: what strains the relationship is how long the
      // promise has been outstanding, and an undated compromiso still ages.
      const dias = r.createdAt
        ? Math.floor((Date.now() - Number(r.createdAt)) / 86400000)
        : 0;
      if (dias > g.masViejoDias) g.masViejoDias = dias;
    });

  return [...porAfianzadora.values()]
    .map((g) => ({ ...g, fiados: g.fiados.size }))
    // Overdue count first — that is what gets a despacho cut off — then volume.
    .sort((a, b) => (b.vencidos - a.vencidos) || (b.total - a.total));
}

// What just happened to a pendiente, denormalized on the doc so the owner
// can watch the team move in real time from the org listener (a feed sorted
// by ultimaActividad.at) without reading the bitácora.
export const ACTIVIDAD_TIPOS = ['creado', 'editado', 'nota', 'asignado', 'resuelto', 'cancelado'];
export const ACTIVIDAD_LABELS = {
  creado: 'registró',
  editado: 'editó',
  nota: 'comentó',
  asignado: 'asignó',
  resuelto: 'resolvió',
  cancelado: 'canceló',
};

/**
 * The `ultimaActividad` field for a write. Merge into every payload that
 * changes a pendiente.
 *
 * @param {string} tipo  - one of ACTIVIDAD_TIPOS
 * @param {{uid?:string, nombre?:string}|null} quien
 * @param {string|null} [texto]  - the note, or the assignee names
 * @param {number|null} [ts]
 * @return {{ultimaActividad: Object}}
 */
export function buildActividad(tipo, quien = null, texto = null, ts = null) {
  return {
    ultimaActividad: {
      tipo: ACTIVIDAD_TIPOS.includes(tipo) ? tipo : 'editado',
      por: quien?.uid || null,
      nombre: quien?.nombre || null,
      texto: texto ? String(texto).slice(0, 140) : null,
      at: ts || Date.now(),
    },
  };
}

/**
 * The update payload that resolves a pendiente. Kept here so every caller
 * stamps the same fields.
 *
 * @param {Object} args
 * @param {Object} args.resueltoCon  - {tipo:'documento'|'evento'|'manual', ...}
 * @param {Object} [args.user]
 * @param {number} [args.ts]
 * @return {Object}
 */
export function buildResolucion({ resueltoCon, user = null, nombre = null, ts = null }) {
  const now = ts || Date.now();
  return {
    estatus: PENDIENTE_ESTATUS.RESUELTO,
    resueltoCon: resueltoCon || { tipo: 'manual' },
    resueltoAt: now,
    resueltoPor: user?.uid || null,
    updatedAt: now,
    ...buildActividad('resuelto', { uid: user?.uid, nombre }, null, now),
  };
}

/**
 * The update payload that cancels a pendiente.
 * @param {{user?:Object, nombre?:string, ts?:number}} args
 * @return {Object}
 */
export function buildCancelacion({ user = null, nombre = null, ts = null } = {}) {
  const now = ts || Date.now();
  return {
    estatus: PENDIENTE_ESTATUS.CANCELADO,
    updatedAt: now,
    ...buildActividad('cancelado', { uid: user?.uid, nombre }, null, now),
  };
}

/**
 * Append an internal note. Never leaves the CRM.
 *
 * @param {Object} pendiente
 * @param {string} texto
 * @param {Object} [user]
 * @param {number} [ts]
 * @return {Array<Object>} the new notasInternas array
 */
export function agregarNotaInterna(pendiente, texto, user = null, ts = null) {
  const now = ts || Date.now();
  const limpio = String(texto || '').trim().slice(0, NOTA_MAX_LEN);
  if (!limpio) return Array.isArray(pendiente?.notasInternas) ? pendiente.notasInternas : [];
  return [
    ...(Array.isArray(pendiente?.notasInternas) ? pendiente.notasInternas : []),
    { texto: limpio, autor: user?.uid || null, at: now },
  ];
}
